import { axios } from "../http/axios.js";
import { api } from '../../constants/api';
import moment from 'moment';

const shopService = {
    // Category operations
    fetchCategories: async () => {
        try {
            const response = await axios.get(api.shop.fetchCategories);
            return response.data;
        } catch (error) {
            console.error("Error fetching categories", error);
            throw error;
        }
    },

    fetchReadyMadeCategories: async () => {
        try {
            const response = await axios.get(api.shop.fetchReadyMadeCategories);
            return response.data;
        } catch (error) {
            console.error("Error fetching ready-made categories", error);
            throw error;
        }
    },

    fetchShopProductsByCategory: async (category, params = {}) => {
        try {
            const response = await axios.get(api.shop.fetchProductsByCategory(category), {
                params
            });
            return response.data;
        } catch (error) {
            console.error("Error fetching shop products by category", error);
            throw error;
        }
    },

    duplicateProduct: async (productId, data) => {
        try {
            const response = await axios.post(api.shop.duplicateProduct(productId), data);
            return response.data;
        } catch (error) {
            console.error("Error duplicating product", error);
            throw error;
        }
    },

    deleteProductImage: async (productId, imageUrl) => {
        try {
            const response = await axios.delete(`${api.shop.deleteProductImage(productId)}`, { data: { imageUrl } });
            return response.data;
        } catch (error) {
            console.error("Error deleting product image", error);
            throw error;
        }
    },

    createCategory: async (categoryData) => {
        try {
            // Log FormData contents (for debugging)
            console.log("Creating category with data:");
            for (let [key, value] of categoryData.entries()) {
                console.log(key, value);
            }

            const response = await axios.post(api.shop.createCategory, categoryData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            return response.data;
        } catch (error) {
            console.error("Error creating category", error);
            throw error;
        }
    },

    updateCategory: async (id, formData) => {
        try {
            const response = await axios.put(api.shop.updateCategory(id), formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            return response.data;
        } catch (error) {
            console.error("Error updating category", error);
            throw error;
        }
    },

    deleteCategory: async (categoryId) => {
        try {
            const response = await axios.delete(api.shop.deleteCategory(categoryId));
            return response.data;
        } catch (error) {
            console.error('Error deleting category:', error);
            throw error;
        }
    },

    // New category related functions
    getCategoryById: async (id) => {
        try {
            const response = await axios.get(api.shop.getCategoryById(id));
            return response.data;
        } catch (error) {
            console.error("Error fetching category by ID", error);
            throw error;
        }
    },

    updateCategoryStatus: async (id, status) => {
        try {
            const response = await axios.put(api.shop.updateCategoryStatus(id), { status });
            return response.data;
        } catch (error) {
            console.error("Error updating category status", error);
            throw error;
        }
    },
    updateCategorySortOrder: async (id, sortOrder) => {
        try {
            const response = await axios.put(api.shop.updateCategorySortOrder(id), { sortOrder });
            return response.data;
        } catch (error) {
            console.error("Error updating category sort order", error);
            throw error;
        }
    },

    updateCategoriesSortOrder: async (categories) => {
        try {
            const response = await axios.patch(api.shop.updateCategoriesSortOrder, { categories });
            return response.data;
        } catch (error) {
            console.error("Error updating categories sort order", error);
            throw error;
        }
    },

    updateCollectionsSortOrder: async (collections) => {
        try {
            const response = await axios.patch(api.shop.updateCollectionsSortOrder, { updates: collections });
            return response.data;
        } catch (error) {
            console.error("Error updating collections sort order", error);
            throw error;
        }
    },

    updateProductsSortOrder: async (products) => {
        try {
            const response = await axios.patch(api.shop.updateProductsSortOrder, { updates: products });
            return response.data;
        } catch (error) {
            console.error("Error updating products sort order", error);
            throw error;
        }
    },

    // Collection operations
    fetchCollections: async () => {
        try {
            const response = await axios.get(api.shop.fetchCollections);
            return response.data;
        } catch (error) {
            console.error("Error fetching collections", error);
            throw error;
        }
    },

    getCollectionsForCategory: async (categoryId) => {
        try {
            const response = await axios.get(api.shop.getCollectionsForCategory(categoryId));
            return response.data;
        } catch (error) {
            console.error('Error fetching collections for category:', error);
            throw error;
        }
    },

    createCollection: async (collectionData) => {
        try {
            // Log FormData contents (for debugging)
            console.log("Creating collection with data:");
            for (let [key, value] of collectionData.entries()) {
                console.log(key, value);
            }

            const response = await axios.post(api.shop.createCollection, collectionData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            return response.data;
        } catch (error) {
            console.error("Error creating collection", error);
            throw error;
        }
    },

    updateCollection: async (id, collectionData) => {
        try {
            // Log FormData contents (for debugging)
            console.log(`Updating collection ${id} with data:`);
            for (let [key, value] of collectionData.entries()) {
                console.log(key, value);
            }

            const response = await axios.put(api.shop.updateCollection(id), collectionData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            return response.data;
        } catch (error) {
            console.error("Error updating collection", error);
            throw error;
        }
    },

    deleteCollection: async (id) => {
        try {
            const response = await axios.delete(api.shop.deleteCollection(id));
            return response.data;
        } catch (error) {
            console.error("Error deleting collection", error);
            throw error;
        }
    },
    // New collection related functions
    getCollectionById: async (id) => {
        try {
            const response = await axios.get(api.shop.getCollectionById(id));
            return response.data;
        } catch (error) {
            console.error("Error fetching collection by ID", error);
            throw error;
        }
    },

    updateCollectionStatus: async (id, status) => {
        try {
            const response = await axios.put(api.shop.updateCollectionStatus(id), { status });
            return response.data;
        } catch (error) {
            console.error("Error updating collection status", error);
            throw error;
        }
    },
    fetchProducts: async (params = {}) => {
        try {
            const response = await axios.get(api.shop.fetchProducts, {
                params: {
                    page: params.page || 1,
                    limit: params.limit || 10,
                    status: params.status,
                    search: params.search,
                    isExclusiveProduct: params.isExclusiveProduct,
                    productType: params.productType,
                    sortBy: params.sortBy,
                    sortOrder: params.sortOrder
                }
            });

            return response.data;
        } catch (error) {
            console.error("Error fetching products", error);
            throw error;
        }
    },

    createProduct: async (productData) => {
        try {
            const response = await axios.post(api.shop.createProduct, productData);
            console.log('Server response:', response.data);
            return response.data;
        } catch (error) {
            console.error("Error creating product", error);
            throw error;
        }
    },


    updateProduct: async (id, productData) => {
        try {
            const response = await axios.put(api.shop.updateProduct(id), productData);
            return response.data;
        } catch (error) {
            console.error("Error updating product", error);
            throw error;
        }
    },

    closeDialogBox: async (settings) => {
        try {
            const response = await axios.post(api.ecommerceShop.closeDialogBox, settings);
            return response.data;
        } catch (error) {
            console.error("Error updating user settings", error);
            throw error;
        }
    },

    fetchDialogSettings: async () => {
        try {
            const response = await axios.post(api.ecommerceShop.fetchDialogSettings);
            return response.data;
        } catch (error) {
            console.error("Error fetching product", error);
            throw error;
        }
    },

    fetchProduct: async (id) => {
        try {
            const response = await axios.get(api.shop.fetchProduct(id));
            return response.data;
        } catch (error) {
            console.error("Error fetching product", error);
            throw error;
        }
    },

    deleteProduct: async (id) => {
        try {
            const response = await axios.delete(api.shop.deleteEcommerceProduct(id));
            return response.data;
        } catch (error) {
            console.error("Error deleting product", error);
            throw error;
        }
    },

    generateDescription: async (productData) => {
        try {
            const response = await axios.post(api.shop.generateDescription, productData);
            return response.data;
        } catch (error) {
            console.error("Error generating AI description", error);
            throw error;
        }
    },

    // Image upload
    uploadImage: async (imageFile) => {
        try {
            const formData = new FormData();
            formData.append('image', imageFile);
            const response = await axios.post(api.shop.uploadImage, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            return response.data;
        } catch (error) {
            console.error("Error uploading image", error);
            throw error;
        }
    },

    // 3D model upload
    upload3DModel: async (modelFile) => {
        try {
            const formData = new FormData();
            formData.append('threeDModel', modelFile);
            const response = await axios.post(api.shop.upload3DModel, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            return response.data;
        } catch (error) {
            console.error("Error uploading 3D model", error);
            throw error;
        }
    },

    fetchOrders: async (params) => {
        try {
            const response = await axios.get(api.shop.fetchOrders, { params });
            return response.data;
        } catch (error) {
            console.error("Error fetching orders", error);
            throw error;
        }
    },

    updateOrderStatus: async (id, status) => {
        try {
            const response = await axios.put(api.shop.updateOrderStatus(id), { status });
            return response.data;
        } catch (error) {
            console.error("Error updating order status", error);
            throw error;
        }
    },

    updateOrderFulfillmentStatus: async (id, fulfillmentStatus) => {
        try {
            const response = await axios.put(api.shop.updateOrderStatus(id), { fulfillmentStatus });
            return response.data;
        } catch (error) {
            console.error("Error updating order fulfillment status", error);
            throw error;
        }
    },

    getOrderStatistics: async (params = {}) => {
        try {
            const response = await axios.get(api.shop.getOrderStatistics, { params });
            return response.data;
        } catch (error) {
            console.error("Error fetching order statistics", error);
            throw error;
        }
    },
    // Shop metrics
    fetchShopMetrics: async (startDate, endDate) => {
        try {
            // Get the token directly from localStorage
            const token = localStorage.getItem("SparkleAuthToken");
            
            const response = await axios.get(api.metrics.getShopMetrics, {
                params: {
                    startDate: startDate ? moment(startDate).format('MM-DD-YYYY') : null,
                    endDate: endDate ? moment(endDate).format('MM-DD-YYYY') : null
                },
                headers: {
                    "x-access-token": token
                }
            });
            return response.data;
        } catch (error) {
            console.error("Error fetching shop metrics", error);
            throw error;
        }
    },    fetchOrderById: async (id) => {
        try {
            const response = await axios.get(api.shop.fetchOrderById(id));
            
            // Log to verify uploadedDesignFiles are returned
            console.log('🔍 [ShopService] Order details response:', {
                orderId: id,
                hasItems: !!response.data.items,
                itemsCount: response.data.items?.length || 0,
                totalUploadedFiles: response.data.items?.reduce((total, item) => 
                    total + (item.uploadedDesignFiles?.length || 0), 0) || 0,
                itemsWithUploadedFiles: response.data.items?.filter(item => 
                    item.uploadedDesignFiles && item.uploadedDesignFiles.length > 0).length || 0
            });
            
            return response.data;
        } catch (error) {
            console.error("Error getting order by id", error);
            throw error;
        }
    },

    refundOrder: async (orderId, amount, reason, items, sendNotification) => {
        try {
            const response = await fetch(api.shop.refundOrder(orderId), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`, // Assuming you're using token-based auth
                },
                body: JSON.stringify({ amount, reason, items, sendNotification }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to process refund');
            }

            return await response.json();
        } catch (error) {
            console.error('Error in refundOrder:', error);
            throw error;
        }
    },

    // Public product endpoints
    fetchPublicProducts: async (params = {}) => {
        try {
            const response = await axios.get(api.ecommerceShop.fetchPublicProducts, {
                params
            });
            return response.data;
        } catch (error) {
            console.error("Error fetching public products", error);
            throw error;
        }
    },

    fetchProductsByCategory: async (category, params = {}) => {
        try {
            const response = await axios.get(api.public.fetchProductsByCategory(category), {
                params
            });
            return response.data;
        } catch (error) {
            console.error("Error fetching products by category", error);
            throw error;
        }
    },

    fetchProductByPath: async (category, collection, productSlug) => {
        try {
            const response = await axios.get(api.public.fetchProduct(category, collection, productSlug));
            return response.data;
        } catch (error) {
            console.error("Error fetching product by path", error);
            throw error;
        }
    },

    // Checkout related functions
    createCheckoutSession: async (checkoutData) => {
        try {
            const response = await axios.post(api.ecommerceShop.createCheckoutSession, checkoutData);
            return response.data;
        } catch (error) {
            console.error("Error creating checkout session", error);
            throw error;
        }
    },
    createDirectCheckoutSession: async (checkoutData) => {
        try {
            // Validate checkout data before sending to API
            if (!checkoutData || (Array.isArray(checkoutData) && checkoutData.length === 0)) {
                throw new Error('No checkout items provided');
            }
            
            // Check that each cart item has required fields
            if (Array.isArray(checkoutData)) {
                checkoutData.forEach((item, index) => {
                    if (!item.productId) {
                        throw new Error(`Item at index ${index} is missing productId`);
                    }
                });
            }
            
            const response = await axios.post(api.ecommerceShop.createDirectCheckoutSession, {
                cartItems: checkoutData
            });
            return response.data;
        } catch (error) {
            console.error("Error creating direct checkout session", error);
            throw error;
        }
    },
    fetchOrderDetails: async (sessionId) => {
        try {
            const response = await axios.get(api.ecommerceShop.fetchOrderDetails(sessionId));
            return response.data;
        } catch (error) {
            console.error("Error fetching order details", error);
            throw error;
        }
    },

    // Customers operations
    fetchCustomers: async (params) => {
        try {
            // Get the token directly from localStorage
            const token = localStorage.getItem("SparkleAuthToken");

            // Include the token explicitly in the request headers
            const response = await axios.get(api.shop.fetchCustomers, {
                params,
                headers: {
                    "x-access-token": token
                }
            });
            return response.data;
        } catch (error) {
            console.error("Error fetching customers", error);
            throw error;
        }
    },

    fetchCustomerById: async (id, params) => {
        try {
            // Get the token directly from localStorage
            const token = localStorage.getItem("SparkleAuthToken");

            // Include the token explicitly in the request headers
            const response = await axios.get(api.shop.fetchCustomerById(id), {
                params,
                headers: {
                    "x-access-token": token
                }
            });
            return response.data;
        } catch (error) {
            console.error("Error fetching customer details", error);
            throw error;
        }
    },
    fetchCustomerOrders: async (id, params) => {
        try {
            // Get the token directly from localStorage
            const token = localStorage.getItem("SparkleAuthToken");            
            // Use the dedicated shop customer orders endpoint
            const response = await axios.get(api.shop.fetchCustomerOrders(id), {
                params,
                headers: {
                    "x-access-token": token
                }
            });
            return response.data;
        } catch (error) {
            console.error("Error fetching customer orders", error);
            throw error;
        }
    },

    // Creator management
    fetchCreators: async (params) => {
        try {
            // Get the token directly from localStorage
            const token = localStorage.getItem("SparkleAuthToken");            
            const response = await axios.get(api.shop.fetchCreators, {
                params,
                headers: {
                    "x-access-token": token
                }
            });
            return response.data;
        } catch (error) {
            console.error("Error fetching creators", error);
            throw error;
        }
    },

    fetchCreatorStats: async () => {
        try {
            // Get the token directly from localStorage
            const token = localStorage.getItem("SparkleAuthToken");            
            const response = await axios.get(api.shop.fetchCreatorStats, {
                headers: {
                    "x-access-token": token
                }
            });
            return response.data;
        } catch (error) {
            console.error("Error fetching creator stats", error);
            throw error;
        }
    },

    fetchCreatorById: async (id) => {
        try {
            // Get the token directly from localStorage
            const token = localStorage.getItem("SparkleAuthToken");            
            const response = await axios.get(api.shop.fetchCreatorById(id), {
                headers: {
                    "x-access-token": token
                }
            });
            return response.data;
        } catch (error) {
            console.error("Error fetching creator", error);
            throw error;
        }
    },

    updateCreator: async (id, updateData) => {
        try {
            const response = await axios.put(api.shop.updateCreator(id), updateData);
            return response.data;
        } catch (error) {
            console.error("Error updating creator", error);
            throw error;        }
    },

    fetchCreatorProductSales: async (id, params) => {
        try {
            // Get the token directly from localStorage
            const token = localStorage.getItem("SparkleAuthToken");            
            const response = await axios.get(api.shop.fetchCreatorProductSales(id), {
                params,
                headers: {
                    "x-access-token": token
                }
            });
            return response.data;
        } catch (error) {
            console.error("Error fetching creator product sales", error);
            throw error;
        }
    },
};

export default shopService;